import type { TimeZone } from '@iheartjane/types';
import { timezone } from '@iheartjane/utils';
import { differenceInYears, isValid, parseISO } from 'date-fns';

import type { Identification } from '@jane/shared/types';

export const isEligibleForGroup = ({
  document,
  timezone,
  groupType,
  requiredAge,
  store,
  verificationType,
}: {
  document: Identification;
  groupType: string;
  requiredAge?: number | null;
  store: {
    medical: boolean;
    state: string | null;
  };
  timezone: TimeZone;
  verificationType?: string | null;
}) => {
  switch (groupType) {
    case 'student': {
      return !!document.student_photo;
    }
    case 'pediatric': {
      const { birth_date } = document;
      if (!birth_date) return false;

      const ageAtStore = ageAtTimezone(birth_date, timezone);
      return !!(ageAtStore && requiredAge && ageAtStore <= requiredAge);
    }
    case 'ssi': {
      return !!document.government_photo;
    }
    case 'snap': {
      return !!document.government_photo;
    }
    case 'industry': {
      return !!document.government_photo;
    }
    case 'medicaid': {
      return !!document.government_photo;
    }
    case 'chip': {
      return !!document.government_assistance_photo;
    }
    case 'pace': {
      return !!document.government_assistance_photo;
    }
    case 'pacenet': {
      return !!document.government_assistance_photo;
    }
    case 'wic': {
      return !!document.government_assistance_photo;
    }
    case 'PA Gov': {
      return !!document.government_assistance_photo;
    }
    case 'medicare': {
      return !!document.government_photo;
    }
    case 'member': {
      return true;
    }
    case 'locals': {
      return !!document.government_photo;
    }
    case 'veteran': {
      return !!document.veteran_photo;
    }
    case 'medical': {
      if (store.state === 'Ohio' && store.medical) return true;

      if (verificationType === 'number') return !!document.mmj_card_id_number;

      return !!(document.mmj_card && document.mmj_card_back);
    }
    case 'senior': {
      const { birth_date } = document;
      if (!birth_date) return false;

      const ageAtStore = ageAtTimezone(birth_date, timezone);
      return !!(ageAtStore && requiredAge && ageAtStore >= requiredAge);
    }
    default: {
      return false;
    }
  }
};

// Unmapped zone names pass straight through and make Intl throw.
const todayInZone = (tz: TimeZone): Date => {
  const zone = timezone(tz) || 'America/Los_Angeles';

  try {
    // en-CA gives YYYY-MM-DD, which parseISO reads as a local date.
    return parseISO(
      new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(new Date())
    );
  } catch {
    return new Date();
  }
};

// V1::DocumentSerializer now sends a date string, but tolerate the epoch *seconds* to prevent regressions.
const toDate = (value: string | number | Date): Date | null => {
  const parsed =
    typeof value === 'string'
      ? parseISO(value)
      : new Date(typeof value === 'number' ? value * 1000 : value);

  return isValid(parsed) ? parsed : null;
};

// Exact diff, not dayjs fromNow(), which rounded 64y11m up to 65.
export const ageAtTimezone = (
  birthDate: string | number | Date | undefined | null,
  timezone: TimeZone
) => {
  const birth = birthDate ? toDate(birthDate) : null;
  if (!birth) return undefined;

  return differenceInYears(todayInZone(timezone), birth);
};
